import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { DirectoryAccount } from '@workflow/contracts'
import { Avatar, Button, Dialog, ENTER, EmptyNote, IconButton, Icons, Loading, StateNote, staggerDelay } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useApproveMember, useMember, useMembers, useRejectMember } from '@/lib/api/hooks'
import { formatTimestamp } from '@/lib/format'
import { personName, realName } from '@/lib/people'
import { ProfileReview } from './ProfileReview'

/**
 * The queue of pending requests — the studio's daily administrative work, and
 * the reason it lives at the top of People: the decision waits for a person,
 * and the page says so by putting it first.
 *
 * A request is the person and their profile. The row answers who and when,
 * with their face; opening it raises the profile in a floating window with
 * the decision at its foot; the two verbs also sit at the row's end,
 * and both are reversible — rejection keeps the account, and whitelisting
 * the address again re-opens the door.
 */
export function RequestsPane() {
  const members = useMembers()
  const requests = members.data?.filter((member) => member.status === 'pending') ?? []

  return (
    <div>
      {members.isLoading ? <Loading label="Loading requests" /> : null}
      {members.error || !members.data ? (
        <StateNote
          title="The requests could not be loaded."
          detail={describeError(members.error)}
          action={
            <Button size="sm" icon={<Icons.retry />} onClick={() => void members.refetch()}>
              Try again
            </Button>
          }
        />
      ) : requests.length ? (
        <ul className="flex flex-col">
          <AnimatePresence initial={false}>
            {requests.map((member, index) => (
              <RequestRow key={member.id} member={member} index={index} />
            ))}
          </AnimatePresence>
        </ul>
      ) : (
        <EmptyNote
          title="No requests are waiting."
          detail="When someone submits their profile for review, it appears here."
        />
      )}
    </div>
  )
}

function RequestRow({ member, index }: { member: DirectoryAccount; index: number }) {
  const [open, setOpen] = useState(false)
  const approve = useApproveMember()
  const reject = useRejectMember()
  const busy = approve.isPending || reject.isPending
  const decisionError = approve.error ?? reject.error

  return (
    <motion.li
      layout="position"
      initial={ENTER.initial}
      animate={{ ...ENTER.animate, transition: { ...ENTER.animate.transition, delay: staggerDelay(index) } }}
      exit={ENTER.exit}
      className="border-b border-hairline last:border-0"
    >
      <div className="-mx-2 flex items-center gap-3 rounded-2xl px-2 py-3 transition-colors duration-fast hover:bg-tint">
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={`Review ${personName(member)}`}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-xl text-left outline-none focus-visible:ring-1 focus-visible:ring-foreground"
        >
          <Avatar name={personName(member)} email={member.email} src={member.pictureUrl} size="sm" />
          <span className="min-w-0">
            <span className="block truncate text-ui font-medium text-foreground">{personName(member)}</span>
            <span className="block truncate text-caption text-muted-foreground">
              {[realName(member), `Requested ${formatTimestamp(member.updatedAt)}`].filter(Boolean).join(' · ')}
            </span>
          </span>
        </button>
        <Decision busy={busy} onApprove={() => approve.mutate(member.id)} onReject={() => reject.mutate(member.id)} />
      </div>
      {decisionError ? <p className="pb-2 text-right text-caption text-danger">{describeError(decisionError)}</p> : null}

      <Dialog open={open} onClose={() => setOpen(false)} labelledBy={`request-${member.id}`} className="sm:max-w-2xl">
        {open ? (
          <div className="flex max-h-[calc(100dvh-2rem)] flex-col rounded-3xl border bg-surface shadow-modal sm:max-h-[calc(100dvh-4rem)]">
            <div className="flex items-center gap-3 px-6 pt-5 pb-4 sm:px-8">
              <Avatar name={personName(member)} email={member.email} src={member.pictureUrl} size="md" />
              <div className="min-w-0 flex-1">
                <h2 id={`request-${member.id}`} className="truncate text-ui font-medium text-foreground">{personName(member)}</h2>
                <p className="truncate text-caption text-muted-foreground">{`Requested ${formatTimestamp(member.updatedAt)}`}</p>
              </div>
              <IconButton label="Close" size="sm" onClick={() => setOpen(false)} tooltipSide="bottom"><Icons.close /></IconButton>
            </div>
            <div className="min-h-0 overflow-y-auto px-6 pb-6 sm:px-8">
              <RequestProfile id={member.id} />
            </div>
            <div className="flex gap-2 border-t border-hairline px-6 py-4 sm:justify-end sm:px-8">
              {decisionError ? <p className="mr-auto self-center text-caption text-danger">{describeError(decisionError)}</p> : null}
              <Button variant="danger" icon={<Icons.close />} busy={reject.isPending} disabled={busy}
                onClick={() => reject.mutate(member.id, { onSuccess: () => setOpen(false) })} className="flex-1 sm:flex-none">
                Reject
              </Button>
              <Button variant="primary" icon={<Icons.approve />} busy={approve.isPending} disabled={busy}
                onClick={() => approve.mutate(member.id, { onSuccess: () => setOpen(false) })} className="flex-1 sm:flex-none">
                Approve
              </Button>
            </div>
          </div>
        ) : null}
      </Dialog>
    </motion.li>
  )
}

function Decision({ busy, onApprove, onReject }: { busy: boolean; onApprove: () => void; onReject: () => void }) {
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <Button size="sm" variant="primary" icon={<Icons.approve />} busy={busy} onClick={onApprove}>
        Approve
      </Button>
      <Button size="sm" variant="danger" icon={<Icons.close />} busy={busy} onClick={onReject}>
        Reject
      </Button>
    </div>
  )
}

function RequestProfile({ id }: { id: string }) {
  const detail = useMember(id)
  if (detail.isLoading) return <Loading label="Loading the profile" />
  if (detail.error || !detail.data) {
    return <StateNote title="This profile could not be loaded." detail={describeError(detail.error)} />
  }
  return <ProfileReview member={detail.data} />
}
